import React from 'react';
import { DocumentPlusIcon, DocumentTextIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatTime } from './date';
import type { CalendarAppointment } from './types';

export interface ApptRowProps {
    appointment: CalendarAppointment;
    isPast: boolean;
    needsNote: boolean;
    /** Hides the cancel icon and the "Add chart note" nudge; the note-chip
     *  indicator still shows when the appointment already has one. */
    readOnly: boolean;
    /** Shows `appointment.practiceName` under the patient name, for a
     *  multi-practice, view-only consumer. */
    showPractice?: boolean;
    /** Replaces the plain practice-name line when `showPractice` is set. */
    renderPractice?: (appointment: CalendarAppointment) => React.ReactNode;
    renderAvatar: (appointment: CalendarAppointment) => React.ReactNode;
    renderStage?: (appointment: CalendarAppointment) => React.ReactNode;
    onSelect: (appointment: CalendarAppointment) => void;
    onCancel?: (appointment: CalendarAppointment) => void;
    onAddNote?: (appointment: CalendarAppointment) => void;
}

export const ApptRow: React.FC<ApptRowProps> = ({
    appointment,
    isPast,
    needsNote,
    readOnly,
    showPractice,
    renderPractice,
    renderAvatar,
    renderStage,
    onSelect,
    onCancel,
    onAddNote
}) => {
    const timeLabel = formatTime(appointment.time) || 'No time';
    const timeClass = isPast ? 'text-gray-400' : 'text-gray-700';

    return (
        <div
            className="group flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50/80"
            onClick={() => onSelect(appointment)}
        >
            <div className="w-[86px] shrink-0">
                <span className={`text-[13px] tabular-nums ${timeClass}`}>{timeLabel}</span>
            </div>
            <div className="h-8 w-8 shrink-0">{renderAvatar(appointment)}</div>
            <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-medium text-gray-800 truncate">
                    {appointment.patientName}
                </div>
                <div className="text-xs text-gray-500 truncate">
                    {appointment.reason || 'No reason given'}
                </div>
                {showPractice &&
                    (renderPractice ? (
                        <div className="text-[11px] truncate">{renderPractice(appointment)}</div>
                    ) : (
                        appointment.practiceName && (
                            <div className="text-[11px] text-gray-400 truncate">
                                {appointment.practiceName}
                            </div>
                        )
                    ))}
            </div>
            {renderStage && <div className="shrink-0">{renderStage(appointment)}</div>}
            {!readOnly && needsNote ? (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAddNote?.(appointment);
                    }}
                    className="shrink-0 flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                >
                    <DocumentPlusIcon className="h-3.5 w-3.5" />
                    Add chart note
                </button>
            ) : (
                appointment.hasNote && (
                    <span
                        title="Chart note attached"
                        className="shrink-0 flex items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-[11px] text-gray-600"
                    >
                        <DocumentTextIcon className="h-3 w-3" />
                    </span>
                )
            )}
            {!readOnly && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCancel?.(appointment);
                    }}
                    aria-label="Cancel appointment"
                    className="shrink-0 h-7 w-7 grid place-items-center rounded-lg text-gray-300 hover:bg-white hover:text-red-600 border border-transparent hover:border-gray-200"
                >
                    <TrashIcon className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
};

export default ApptRow;
